import { Stack, Tooltip, Typography } from '@mui/material';
import {
  ActionButton,
  SelectionDialogActions,
  SelectionDialogContent,
  contentStyles,
  paperStyles,
  titleStyles,
  tooltipStyles,
} from './styles';
import { abyssColors } from '@/theme/colors';
import { formatKeyForDisplay } from '@/utils';
import { Restore } from '@mui/icons-material';
import { GenericDialog } from '@/components/shared/GenericDialog';
import {
  riskInputKeysObject,
  riskOutputKeysObject,
} from '../../../Analysis/RiskAssessment/constants';

type ModalProps = {
  open: boolean;
  handleClose: () => void;
  updatedData: Record<string, unknown>;
  title: string;
  showOnlyLastLabel?: boolean;
  isESVersion?: boolean;
  allowRestore?: boolean;
  handleRestoreClick?: () => void;
};

const renderValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return (
      <Stack sx={{ marginLeft: '0.8rem' }}>
        {value.map((item, index) => (
          <Typography key={index} fontSize="1.6rem" color={abyssColors.primary[500]}>
            {renderValue(item)}
          </Typography>
        ))}
      </Stack>
    );
  } else if (typeof value === 'object' && value !== null) {
    return (
      <Stack
        marginBottom="1.6rem"
        width="100%"
        borderTop={`1px solid ${abyssColors.neutral[100]}`}
        paddingTop="0.8rem"
      >
        {Object.entries(value).map(([subKey, subValue]) => (
          <Stack key={subKey} direction="column" gap="0.4rem" marginLeft="0.8rem" width="100%">
            <Typography fontSize="1.2rem" fontWeight={500} color={abyssColors.primary[300]}>
              {formatKeyForDisplay(subKey)}:
            </Typography>
            <Typography fontSize="1.6rem" color={abyssColors.primary[500]}>
              {renderValue(subValue)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    );
  }
  return String(value);
};

const cleanKeys = (updatedData: Record<string, unknown>) => {
  const cleanedData: Record<string, unknown> = {};

  Object.keys(updatedData).forEach((key) => {
    const newKey = key.replace('templateFieldData.', ''); // Remove the prefix
    cleanedData[newKey] = updatedData[key]; // Assign the value with new key
  });

  return cleanedData;
};

const ESVersion = ({
  updatedData,
  type,
}: {
  updatedData: Record<string, unknown>;
  type: string;
}) => {
  const data = cleanKeys(updatedData);
  return (
    <>
      <Typography fontSize="1.2rem" color={abyssColors.primary[500]} fontWeight={600}>
        {type.replace('calculation', '')} input values:
      </Typography>
      <Stack gap="24px" marginY="15px">
        {Object.keys(data)
          .filter((key) => key in riskInputKeysObject)
          .map((key) => (
            <Stack
              borderBottom={`1px solid ${abyssColors.neutral[200]}`}
              gap={0.5}
              paddingBottom="12px"
              key={key}
            >
              <Typography fontSize="1.2rem" color={abyssColors.primary[300]}>
                {riskInputKeysObject[key]}
              </Typography>
              <Tooltip
                title={
                  typeof data[key] !== 'object' && !Array.isArray(data[key])
                    ? renderValue(data[key])
                    : ''
                }
                arrow
                placement="top"
                PopperProps={{
                  sx: { tooltipStyles },
                }}
              >
                <Typography
                  fontSize="1.6rem"
                  color={abyssColors.primary[500]}
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    width: 'fit-content',
                    maxWidth: '100%',
                  }}
                >
                  {renderValue(data[key])}
                </Typography>
              </Tooltip>
            </Stack>
          ))}
      </Stack>
      <Typography
        fontSize="1.2rem"
        color={abyssColors.primary[500]}
        fontWeight={600}
        marginTop="30px"
      >
        {type.replace('calculation', '')} output values:
      </Typography>
      <Stack gap="24px" marginY="15px">
        {Object.keys(data)
          .filter((key) => key in riskOutputKeysObject)
          .map((key) => (
            <Stack
              borderBottom={`1px solid ${abyssColors.neutral[200]}`}
              gap={0.5}
              paddingBottom="12px"
              key={key}
            >
              <Typography fontSize="1.2rem" color={abyssColors.primary[300]}>
                {riskOutputKeysObject[key]}
              </Typography>
              <Tooltip
                title={
                  typeof data[key] !== 'object' && !Array.isArray(data[key])
                    ? renderValue(data[key])
                    : ''
                }
                arrow
                placement="top"
                PopperProps={{
                  sx: { tooltipStyles },
                }}
              >
                <Typography
                  fontSize="1.6rem"
                  color={abyssColors.primary[500]}
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    width: 'fit-content',
                    maxWidth: '100%',
                  }}
                >
                  {renderValue(data[key])}
                </Typography>
              </Tooltip>
            </Stack>
          ))}
      </Stack>
    </>
  );
};

export const VersionUpdatesModal = ({
  open,
  handleClose,
  updatedData = {},
  title = '',
  showOnlyLastLabel = false,
  allowRestore,
  handleRestoreClick = () => {},
  isESVersion = false,
}: ModalProps) => {
  const getDisplayLabel = (key: string) => {
    return showOnlyLastLabel ? key.split('.').pop() || key : key;
  };
  return (
    <GenericDialog
      open={open}
      onClose={handleClose}
      title={title}
      minWidth="0px"
      maxWidth="xl"
      showCloseIcon
      titleStyles={titleStyles}
      paperStyles={paperStyles}
      actionsStyles={{ padding: 0 }}
      contentStyles={contentStyles}
      content={
        <SelectionDialogContent>
          {!isESVersion && (
            <Stack gap="24px">
              {Object.keys(updatedData).map((key) => (
                <Stack
                  borderBottom={`1px solid ${abyssColors.neutral[200]}`}
                  gap={0.5}
                  paddingBottom="12px"
                  key={key}
                >
                  <Typography fontSize="1.2rem" color={abyssColors.primary[300]}>
                    {formatKeyForDisplay(getDisplayLabel(key))}
                  </Typography>
                  <Tooltip
                    title={
                      typeof updatedData[key] !== 'object' && !Array.isArray(updatedData[key])
                        ? renderValue(updatedData[key])
                        : ''
                    }
                    arrow
                    placement="top"
                    PopperProps={{
                      sx: { tooltipStyles },
                    }}
                  >
                    <Typography
                      fontSize="1.6rem"
                      color={abyssColors.primary[500]}
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        width: 'fit-content',
                        maxWidth: '100%',
                      }}
                    >
                      {renderValue(updatedData[key])}
                    </Typography>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          )}
          {isESVersion && (
            <Stack>
              <ESVersion updatedData={updatedData} type={title} />
            </Stack>
          )}
        </SelectionDialogContent>
      }
      actions={
        <SelectionDialogActions>
          <Stack direction="row" gap={2}>
            {allowRestore && (
              <ActionButton variant="outlined" onClick={handleRestoreClick}>
                <Restore sx={{ fontSize: '1.6rem' }} />
                <span>Restore Version</span>
              </ActionButton>
            )}
            <ActionButton variant="contained" contained onClick={handleClose}>
              Got it
            </ActionButton>
          </Stack>
        </SelectionDialogActions>
      }
    />
  );
};
